import { useState, useEffect } from 'react';
import {
  X,
  Image as ImageIcon,
  Wand2,
  Download,
  Check,
  Loader2,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { useStore } from '../store';
import { imageApi } from '../services/imageApi';
import type {
  ImageProvider,
  ImageGenerationRequest,
  GeneratedImage,
} from '../types/images';
import { IMAGE_MODELS } from '../types/images';

interface ImageGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageSelect?: (imageUrl: string, prompt: string) => void;
  initialPrompt?: string;
}

const PROVIDERS: Record<ImageProvider, { name: string }> = {
  openai: { name: 'DALL-E 3' },
  stability: { name: 'Stability AI' },
  leonardo: { name: 'Leonardo AI' },
  gemini: { name: 'Gemini' },
};

const SIZE_PRESETS = [
  { label: 'Square', width: 1024, height: 1024 },
  { label: 'Portrait', width: 1024, height: 1792 },
  { label: 'Landscape', width: 1792, height: 1024 },
];

export function ImageGenerationModal({
  isOpen,
  onClose,
  onImageSelect,
  initialPrompt = '',
}: ImageGenerationModalProps) {
  const {
    imageProvider,
    setImageProvider,
    styleReferences,
    selectedStyleRefs,
    selectedCharacterRefs,
    toggleStyleRefSelection,
    toggleCharacterRefSelection,
    clearRefSelections,
    addGeneratedImage,
    addNotification,
  } = useStore();

  const [prompt, setPrompt] = useState(initialPrompt);
  const [negativePrompt, setNegativePrompt] = useState('');
  const [selectedSize, setSelectedSize] = useState(SIZE_PRESETS[0]);
  const [selectedModel, setSelectedModel] = useState(IMAGE_MODELS[imageProvider][0]?.id || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<GeneratedImage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showStyleLibrary, setShowStyleLibrary] = useState(false);

  useEffect(() => {
    if (initialPrompt) {
      setPrompt(initialPrompt);
    }
  }, [initialPrompt]);

  // Update model when provider changes
  useEffect(() => {
    const models = IMAGE_MODELS[imageProvider];
    if (models && models.length > 0) {
      setSelectedModel(models[0].id);
    }
  }, [imageProvider]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);

    try {
      const request: ImageGenerationRequest = {
        prompt: prompt.trim(),
        provider: imageProvider,
        model: selectedModel,
        width: selectedSize.width,
        height: selectedSize.height,
        negativePrompt: ['stability', 'leonardo'].includes(imageProvider) ? negativePrompt : undefined,
        styleReferenceIds: selectedStyleRefs.length > 0
          ? selectedStyleRefs.map(id => parseInt(id, 10)).filter(id => !isNaN(id))
          : undefined,
      };

      const result = await imageApi.generate(request);

      if (result.success && result.image) {
        setGeneratedImage(result.image);
        addGeneratedImage(result.image);
        addNotification('Image generated successfully', 'success');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate image';
      setError(message);
      addNotification(message, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseImage = () => {
    if (generatedImage && onImageSelect) {
      onImageSelect(generatedImage.url, generatedImage.prompt);
      onClose();
    }
  };

  const handleDownload = () => {
    if (generatedImage) {
      const link = document.createElement('a');
      link.href = generatedImage.url;
      link.download = `generated-${Date.now()}.png`;
      link.click();
    }
  };

  if (!isOpen) return null;

  const supportsNegativePrompt = ['stability', 'leonardo'].includes(imageProvider);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent/20 rounded-lg">
              <Wand2 className="w-5 h-5 text-accent" />
            </div>
            <h2 className="text-xl font-semibold">AI Image Generation</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Controls */}
            <div className="space-y-6">
              {/* Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image Provider
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.entries(PROVIDERS) as [ImageProvider, { name: string }][]).map(
                    ([key, { name }]) => (
                      <button
                        key={key}
                        onClick={() => setImageProvider(key)}
                        className={`flex items-center justify-center px-4 py-3 rounded-lg border transition-all ${
                          imageProvider === key
                            ? 'border-accent bg-accent/10 text-accent-dark'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium">{name}</span>
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the image you want to generate..."
                  className="w-full px-4 py-3 border rounded-lg resize-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  rows={4}
                />
              </div>

              {/* Negative Prompt (if supported) */}
              {supportsNegativePrompt && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Negative Prompt (optional)
                  </label>
                  <textarea
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    placeholder="Describe what you don't want in the image..."
                    className="w-full px-4 py-3 border rounded-lg resize-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    rows={2}
                  />
                </div>
              )}

              {/* Size Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image Size
                </label>
                <div className="flex gap-2">
                  {SIZE_PRESETS.map((size) => (
                    <button
                      key={size.label}
                      onClick={() => setSelectedSize(size)}
                      className={`flex-1 px-4 py-2 rounded-lg border transition-all ${
                        selectedSize.label === size.label
                          ? 'border-accent bg-accent/10 text-accent-dark'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  {IMAGE_MODELS[imageProvider]?.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Style References */}
              {styleReferences.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowStyleLibrary(!showStyleLibrary)}
                    className="text-sm text-accent hover:text-accent-dark font-medium"
                  >
                    {showStyleLibrary ? 'Hide' : 'Show'} Style References (
                    {selectedStyleRefs.length + selectedCharacterRefs.length} selected)
                  </button>
                  {showStyleLibrary && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium">Style Library</span>
                        {(selectedStyleRefs.length > 0 || selectedCharacterRefs.length > 0) && (
                          <button
                            onClick={clearRefSelections}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {styleReferences.slice(0, 8).map((ref) => {
                          const isSelected =
                            selectedStyleRefs.includes(ref.id) ||
                            selectedCharacterRefs.includes(ref.id);
                          return (
                            <button
                              key={ref.id}
                              onClick={() => {
                                if (ref.category === 'character') {
                                  toggleCharacterRefSelection(ref.id);
                                } else {
                                  toggleStyleRefSelection(ref.id);
                                }
                              }}
                              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                                isSelected
                                  ? 'border-accent ring-2 ring-accent/30'
                                  : 'border-transparent hover:border-gray-300'
                              }`}
                            >
                              <img
                                src={ref.thumbnailUrl}
                                alt={ref.name}
                                className="w-full h-full object-cover"
                              />
                              {isSelected && (
                                <div className="absolute inset-0 bg-accent/20 flex items-center justify-center">
                                  <Check className="w-6 h-6 text-white drop-shadow-lg" />
                                </div>
                              )}
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                                <span className="text-xs text-white truncate block">
                                  {ref.name}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
            </div>

            {/* Right: Preview */}
            <div className="space-y-4">
              <div
                className={`aspect-square rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden ${
                  generatedImage ? 'border-transparent' : 'border-gray-300 bg-gray-50'
                }`}
              >
                {isGenerating ? (
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 text-accent animate-spin mx-auto mb-3" />
                    <p className="text-gray-600">Generating your image...</p>
                    <p className="text-sm text-gray-400 mt-1">This may take a moment</p>
                  </div>
                ) : generatedImage ? (
                  <img
                    src={generatedImage.url}
                    alt="Generated"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center p-8">
                    <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Your generated image will appear here</p>
                  </div>
                )}
              </div>

              {/* Revised Prompt */}
              {generatedImage?.revisedPrompt && (
                <div className="p-3 bg-accent/10 rounded-lg">
                  <p className="text-xs font-medium text-accent-dark mb-1">Revised Prompt:</p>
                  <p className="text-sm text-accent">{generatedImage.revisedPrompt}</p>
                </div>
              )}

              {/* Action Buttons */}
              {generatedImage && (
                <div className="flex gap-2">
                  <button
                    onClick={handleGenerate}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Regenerate
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  {onImageSelect && (
                    <button
                      onClick={handleUseImage}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark transition-colors"
                    >
                      <Check className="w-4 h-4" />
                      Use Image
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Images are generated using your configured API keys
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              className="flex items-center gap-2 px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  Generate
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageGenerationModal;
